// components/NotificationsSection.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNotifications } from '../../../contexts/NotificationContext'; // Corrected Import Path
import { formatDistanceToNow } from 'date-fns'; // Import date formatting
import { useNavigate } from 'react-router-dom';

const NOTIFICATION_LIMIT = 5; // Define the limit for latest notifications
const LOCAL_STORAGE_HIDDEN_KEY = 'hiddenNotificationIds';

const NotificationsSection = () => {
  const { notifications, loading, error } = useNotifications(); // Use the hook
  const [hiddenNotificationIds, setHiddenNotificationIds] = useState([]); // State to store hidden notification IDs
  const navigate = useNavigate();

  // Load hidden IDs from local storage on mount
  useEffect(() => {
    const storedHiddenIds = localStorage.getItem(LOCAL_STORAGE_HIDDEN_KEY);
    if (storedHiddenIds) {
      try {
        setHiddenNotificationIds(JSON.parse(storedHiddenIds));
      } catch (e) {
        console.error('Failed to parse hidden notification IDs from localStorage:', e);
        localStorage.removeItem(LOCAL_STORAGE_HIDDEN_KEY); // Clear invalid data
      }
    }
  }, []); // Run only once on mount

  // Save hidden IDs to local storage whenever the state changes
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_HIDDEN_KEY, JSON.stringify(hiddenNotificationIds));
  }, [hiddenNotificationIds]);

  // Function to hide a notification
  const handleHideNotification = (notificationId) => {
    setHiddenNotificationIds(prevIds => {
      if (prevIds.includes(notificationId)) {
        return prevIds; // Already hidden
      }
      return [...prevIds, notificationId];
    });
  };

  // Helper function to format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      // Check if timestamp is a Firestore Timestamp or a standard Date/string
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (e) {
      console.error('Error formatting timestamp:', e);
      return '';
    }
  };

  // Show only latest notifications, excluding hidden ones (memoized)
  const displayedNotifications = useMemo(() => {
    return notifications
      .filter(notif => !hiddenNotificationIds.includes(notif.id))
      .slice(0, NOTIFICATION_LIMIT);
  }, [notifications, hiddenNotificationIds]);

  // Debug logs removed to prevent infinite re-renders

  return (
    <div className="bg-gray-800 p-4 rounded-lg h-full flex flex-col">
      <div className="flex justify-between items-center mb-2">
        <h4 className="text-xl font-bold text-white">Notifications</h4>
        <button
          onClick={() => navigate('/notifications/history')}
          className="text-sm text-blue-400 hover:underline focus:outline-none ml-2"
        >
          Show History
        </button>
      </div>
      {
        // Prioritize displaying notifications if available
        displayedNotifications.length > 0 ? (
          <ul className="space-y-3 overflow-y-auto flex-1 pr-2">
            {displayedNotifications.map((notification) => (
              // Use notification.id for key
              <li key={notification.id} className="border-b border-gray-700 pb-2 last:border-b-0 last:pb-0 flex justify-between items-start">
                <div>
                  <p className="text-white text-sm whitespace-pre-wrap">
                    {notification.type === 'automation' 
                      ? notification.message || 'No message'
                      : notification.log || notification.message || notification.username || 'No message'
                    }
                  </p>
                  <span className="text-xs text-gray-400">{formatTimestamp(notification.timestamp)}</span>
                </div>
              </li>
            ))}
          </ul>
        ) : loading ? ( // Show loading if no displayed notifications and still loading
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-400">Loading notifications...</p>
          </div>
        ) : (notifications.length > 0 && hiddenNotificationIds.length === notifications.length) ? ( // Show hidden message if all are hidden
           <div className="flex-1 flex items-center justify-center">
             <p className="text-gray-400">All notifications hidden.</p>
           </div>
        ) : error ? ( // Show error if no displayed notifications, not loading, and there's an error
          <div className="flex-1 flex items-center justify-center">
            <p className="text-red-400">Error loading notifications: {error.message}</p>
          </div>
        ) : ( // Show no notifications message otherwise
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-400">No notifications yet</p>
          </div>
        )
      }
    </div>
  );
};

export default NotificationsSection;
